import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, ChevronDown, X, HelpCircle, ThumbsUp, ThumbsDown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

const FAQ_SECTIONS = [
  "All",
  "Getting Started",
  "Privacy & Safety",
  "Activities & Hosting",
  "Friends & Connections",
  "Badges & Trust",
  "Businesses & Visibility",
  "Money & Earnings",
  "What LikeKind Is / Isn't"
];

export default function FaqDrawer({ trigger }) {
  const [open, setOpen] = useState(false);
  const [faqItems, setFaqItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSection, setSelectedSection] = useState('All');
  const [feedbackGiven, setFeedbackGiven] = useState({});

  const fetchFaq = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (searchQuery.trim()) {
        params.append('query', searchQuery.trim());
      }
      if (selectedSection && selectedSection !== 'All') {
        params.append('section', selectedSection);
      }
      
      const response = await axios.get(`${API}/api/faq?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFaqItems(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please log in to access the FAQ');
      } else if (error.response?.status === 403) {
        toast.error('FAQ is for individual users only');
      }
      setFaqItems([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedSection]);

  useEffect(() => {
    if (open) {
      fetchFaq();
    }
  }, [open, fetchFaq]);

  // Debounce search
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      fetchFaq();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedSection, open, fetchFaq]);

  const handleFeedback = async (faqId, wasHelpful) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/faq/feedback`, {
        faq_id: faqId,
        was_helpful: wasHelpful,
        context_route: window.location.pathname
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFeedbackGiven(prev => ({ ...prev, [faqId]: wasHelpful }));
      toast.success(wasHelpful ? 'Thanks for the feedback!' : 'Thanks, we\'ll improve this');
    } catch (error) {
      toast.error('Could not submit feedback');
    }
  };

  // Group FAQ items by section
  const groupedFaq = faqItems.reduce((acc, item) => {
    const section = item.section || 'Other';
    if (!acc[section]) acc[section] = [];
    acc[section].push(item);
    return acc;
  }, {});

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {trigger || (
          <button className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
            <HelpCircle className="w-4 h-4" />
            How LikeKind Works
          </button>
        )}
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-indigo-500" />
            How LikeKind Works
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {/* Search & Filter */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={selectedSection} onValueChange={setSelectedSection}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Section" />
              </SelectTrigger>
              <SelectContent>
                {FAQ_SECTIONS.map(section => (
                  <SelectItem key={section} value={section}>
                    {section}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Loading */}
          {loading && (
            <div className="text-center py-8 text-slate-500">
              Loading...
            </div>
          )}

          {/* Results */}
          {!loading && faqItems.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <p>No questions found.</p>
              <p className="text-sm mt-1">Try a different search or section.</p>
            </div>
          )}

          {!loading && Object.keys(groupedFaq).length > 0 && (
            <div className="space-y-6">
              {Object.entries(groupedFaq).map(([section, items]) => (
                <div key={section}>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 uppercase tracking-wide">
                    {section}
                  </h3>
                  <Accordion type="single" collapsible className="space-y-2">
                    {items.map((item) => (
                      <AccordionItem 
                        key={item.id || item.faq_id} 
                        value={item.id || item.faq_id}
                        className="border rounded-lg px-4"
                      >
                        <AccordionTrigger className="text-left text-sm hover:no-underline">
                          {item.question}
                        </AccordionTrigger>
                        <AccordionContent>
                          <p className="text-sm text-slate-600 mb-4">
                            {item.answer}
                          </p>
                          
                          {/* Feedback */}
                          {feedbackGiven[item.faq_id || item.id] !== undefined ? (
                            <p className="text-xs text-emerald-600">
                              ✓ Thanks for your feedback
                            </p>
                          ) : (
                            <div className="flex items-center gap-3 text-xs text-slate-500">
                              <span>Did this answer your question?</span>
                              <button
                                onClick={() => handleFeedback(item.faq_id || item.id, true)}
                                className="flex items-center gap-1 hover:text-emerald-600 transition-colors"
                              >
                                <ThumbsUp className="w-3.5 h-3.5" />
                                Yes
                              </button>
                              <button
                                onClick={() => handleFeedback(item.faq_id || item.id, false)}
                                className="flex items-center gap-1 hover:text-amber-600 transition-colors"
                              >
                                <ThumbsDown className="w-3.5 h-3.5" />
                                Not quite
                              </button>
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
